/*
 * Shared event-type metadata for the audit Log pages.
 *
 * Both the main Log page and the dedicated Expiry Log page import from here
 * so they agree on:
 *   - Which events belong to the "expiry" family (and therefore move to the
 *     dedicated page rather than the generic Log).
 *   - How each type renders (badge label, icon, colour family).
 *
 * Keeping one source of truth means "adding a new event" only needs one edit.
 */

// Every event type the dedicated Expiry Log owns. The main Log page hides
// these and the backend honours `excludeTypes=...` to enforce it server-side.
export const EXPIRY_EVENT_TYPES = Object.freeze([
  "TAG_ENDDATE_SET",
  "TAG_ENDDATE_CLEARED",
  "TAG_NEARING_END",
  "TAG_EXPIRED",
]);

// Colour families for the row-accent + badge. Stays in lockstep with the
// CSS selectors in AuditLog.css (e.g. .log-type-create, .log-row-delete).
export const TYPE_FAMILY = {
  // Auth
  USER_LOGIN:                 { family: "auth-good",     label: "Login",              icon: "fa-sign-in" },
  USER_LOGIN_FAILED:          { family: "auth-bad",      label: "Login failed",       icon: "fa-ban" },
  USER_LOGOUT:                { family: "auth-neutral",  label: "Logout",             icon: "fa-sign-out" },
  // Users
  USER_CREATED:               { family: "create",        label: "User created",       icon: "fa-user-plus" },
  USER_UPDATED:               { family: "update",        label: "User updated",       icon: "fa-user" },
  USER_DELETED:               { family: "delete",        label: "User deleted",       icon: "fa-user-times" },
  // Locations
  LOCATION_CREATED:           { family: "create",        label: "Location created",   icon: "fa-map-marker" },
  LOCATION_UPDATED:           { family: "update",        label: "Location updated",   icon: "fa-map-marker" },
  LOCATION_DELETED:           { family: "delete",        label: "Location deleted",   icon: "fa-map-marker" },
  // Warehouses
  WAREHOUSE_CREATED:          { family: "create",        label: "Warehouse created",  icon: "fa-building" },
  WAREHOUSE_UPDATED:          { family: "update",        label: "Warehouse updated",  icon: "fa-building" },
  WAREHOUSE_DELETED:          { family: "delete",        label: "Warehouse deleted",  icon: "fa-building" },
  WAREHOUSE_TAG_ASSIGN:       { family: "update",        label: "WH tag assigned",    icon: "fa-plus-circle" },
  WAREHOUSE_TAG_REMOVE:       { family: "update",        label: "WH tag removed",     icon: "fa-minus-circle" },
  // Rooms / Partitions
  ROOM_CREATED:               { family: "create",        label: "Partition created",  icon: "fa-cube" },
  ROOM_UPDATED:               { family: "update",        label: "Partition updated",  icon: "fa-cube" },
  ROOM_DELETED:               { family: "delete",        label: "Partition deleted",  icon: "fa-cube" },
  ROOM_TAG_ASSIGN:            { family: "update",        label: "Room tag assigned",  icon: "fa-plus-circle" },
  ROOM_TAG_REMOVE:            { family: "update",        label: "Room tag removed",   icon: "fa-minus-circle" },
  ROOM_TAG_BULK_ASSIGN:       { family: "update",        label: "Room tags assigned (bulk)", icon: "fa-plus-square" },
  ROOM_TAG_BULK_REMOVE:       { family: "update",        label: "Room tags removed (bulk)",  icon: "fa-minus-square" },
  // Products
  PRODUCT_CREATED:            { family: "create",        label: "Product created",    icon: "fa-cubes" },
  PRODUCT_UPDATED:            { family: "update",        label: "Product updated",    icon: "fa-cubes" },
  PRODUCT_DELETED:            { family: "delete",        label: "Product deleted",    icon: "fa-cubes" },
  // Tags
  TAG_CREATED:                { family: "create",        label: "Tag added",          icon: "fa-tag" },
  TAG_UPDATED:                { family: "update",        label: "Tag updated",        icon: "fa-tag" },
  TAG_DELETED:                { family: "delete",        label: "Tag deleted",        icon: "fa-tag" },
  TAG_BULK_UPDATE:            { family: "update",        label: "Tags updated (bulk)",icon: "fa-tags" },
  TAG_BULK_DELETE:            { family: "delete",        label: "Tags deleted (bulk)",icon: "fa-tags" },
  TAG_ALL_DELETED:            { family: "delete-hard",   label: "ALL tags deleted",   icon: "fa-exclamation-triangle" },
  TAG_ADDED:                  { family: "create",        label: "Tag added",          icon: "fa-tag" }, // legacy
  // Expiry (dedicated page)
  TAG_ENDDATE_SET:            { family: "update",        label: "End date set",       icon: "fa-calendar-plus-o" },
  TAG_ENDDATE_CLEARED:        { family: "auth-neutral",  label: "End date cleared",   icon: "fa-calendar-minus-o" },
  TAG_NEARING_END:            { family: "auth-bad",      label: "Nearing end",        icon: "fa-hourglass-half" },
  TAG_EXPIRED:                { family: "delete-hard",   label: "Expired",            icon: "fa-exclamation-circle" },
  // Transfers
  TRANSFER_REQUESTED:         { family: "update",        label: "Transfer requested", icon: "fa-exchange" },
  TRANSFER_ACCEPTED:          { family: "auth-good",     label: "Transfer accepted",  icon: "fa-check" },
  TRANSFER_REJECTED:          { family: "auth-bad",      label: "Transfer rejected",  icon: "fa-times" },
  CHECKIN:                    { family: "auth-good",     label: "Check-in",           icon: "fa-check-circle" },
  // Destructive / admin
  SEED_DATA:                  { family: "system",        label: "Seed data",          icon: "fa-database" },
  DELETE_ALL_DATA:            { family: "delete-hard",   label: "DELETE ALL DATA",    icon: "fa-exclamation-triangle" },
  AUDIT_LOG_SEEDED:           { family: "system",        label: "Log seeded",         icon: "fa-flask" },
  AUDIT_LOG_CLEARED:          { family: "delete-hard",   label: "Log cleared",        icon: "fa-eraser" },
  // RFID-hardware (mirrored from Python)
  THEFT:                      { family: "auth-bad",      label: "Theft alarm",        icon: "fa-bell" },
  CHECKIN_WAREHOUSE_MISMATCH: { family: "auth-bad",      label: "Wrong warehouse",    icon: "fa-exclamation-circle" },
  PAYMENT:                    { family: "auth-good",     label: "Payment",            icon: "fa-credit-card" },
};

export const classifyType = (type) =>
  TYPE_FAMILY[type] || { family: "other", label: type || "Unknown", icon: "fa-circle-o" };
